import React, { useState } from "react";
import { CheckCircle, Prohibit, Truck } from "@phosphor-icons/react";
import { MATERIALS, MATERIAL_ORDER, materialSlug, NOT_ACCEPTED } from "@/lib/materials";

function WasteCard({ name, active, onClick }) {
  const [broken, setBroken] = useState(false);
  const meta = MATERIALS[name];
  if (!meta) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`waste-card-${materialSlug(name)}`}
      aria-pressed={active}
      className={`group relative text-left bg-white border-2 transition-all duration-200 overflow-hidden flex flex-col ${
        active
          ? "border-sky-500 ring-4 ring-sky-200 -translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(14,165,233,0.55)]"
          : "border-slate-900 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_rgba(15,23,42,0.45)]"
      }`}
    >
      <div className="relative h-48 w-full overflow-hidden bg-slate-900">
        {!broken ? (
          <img
            src={meta.photo}
            alt={name}
            loading="lazy"
            onError={() => setBroken(true)}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${meta.fallbackBg}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/15 to-transparent" />
        {active && (
          <div className="absolute top-3 right-3 h-8 w-8 rounded-full bg-sky-500 border-2 border-white flex items-center justify-center shadow-md">
            <CheckCircle size={18} weight="fill" className="text-white" />
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center px-2.5 py-1 bg-white/95 text-slate-900 text-[9px] font-extrabold tracking-[0.18em] uppercase">
            {meta.subtitle}
          </span>
        </div>
        <div className="absolute bottom-3 left-4 right-4">
          <div className="font-display font-black text-2xl text-white drop-shadow leading-tight">
            {name}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col p-5 gap-3">
        <p className="text-sm text-slate-600 leading-relaxed">{meta.blurb}</p>
        <ul className="mt-1 space-y-1.5">
          {meta.accepts.map((a) => (
            <li key={a} className="flex items-start gap-2 text-xs text-slate-700">
              <CheckCircle
                size={14}
                weight="fill"
                className={`mt-0.5 shrink-0 ${active ? "text-sky-500" : "text-emerald-600"}`}
              />
              <span className="font-medium">{a}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={`px-5 py-3 border-t-2 flex items-center justify-between transition-colors ${
          active
            ? "bg-sky-500 border-sky-500 text-white"
            : "bg-slate-50 border-slate-900 text-slate-900 group-hover:bg-slate-900 group-hover:text-white"
        }`}
      >
        <span className="text-[10px] font-extrabold tracking-[0.22em] uppercase">
          {active ? "Selected" : "Choose this"}
        </span>
        <Truck size={16} weight="bold" />
      </div>
    </button>
  );
}

export default function WasteTypeSelector({ value, onChange }) {
  return (
    <div data-testid="waste-type-selector">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {MATERIAL_ORDER.map((name) => (
          <WasteCard
            key={name}
            name={name}
            active={value === name}
            onClick={() => onChange(name)}
          />
        ))}
      </div>

      <div className="mt-8 border-2 border-rose-600 bg-rose-50">
        <header className="flex items-center gap-3 border-b-2 border-rose-600 px-5 py-3 bg-rose-600 text-white">
          <Prohibit size={22} weight="bold" />
          <div>
            <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase opacity-90">
              Prohibited items
            </div>
            <div className="font-display font-bold text-base leading-tight">
              Please don&apos;t put any of these in your bin
            </div>
          </div>
        </header>
        <div className="p-5">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {NOT_ACCEPTED.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 text-sm text-slate-800"
                data-testid={`not-accepted-${item.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
              >
                <Prohibit size={16} weight="bold" className="mt-0.5 shrink-0 text-rose-600" />
                <span className="font-medium">{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-slate-600 leading-relaxed">
            Loading prohibited items can result in extra disposal fees or a refused pickup. When in doubt,
            give us a call before tossing it in — we&apos;ll point you to the right disposal route.
          </p>
        </div>
      </div>
    </div>
  );
}
